import { type FormInstance, Form, Input, InputNumber, Select } from 'antd';

import { CollaboratorChooser } from 'components/choosers/CollaboratorChooser';
import { DeliveryOrganizationChooser } from 'components/choosers/DeliveryOrganizationChooser';
import TagChooser from 'components/choosers/TagChooser';
import { WorkContainerChooser } from 'components/choosers/WorkContainerChooser';
import { UTCDatePicker } from 'components/UTCDatePicker';
import {
  type AtlasGqlGlobalWorkContainerField,
  AtlasGqlWorkContainerFieldType,
  AtlasGqlWorkContainerTypeOption,
} from 'types/atlas-graphql';
import { WORK_ORDER_EDIT_COLLABORATORS } from 'utils/access-control/rules';
import { TASK_STATUSES, WORK_CAMPAIGN, WORK_ORDER } from 'utils/constants';
import { startCase } from 'utils/format';
import { RenderWithAccess } from 'utils/usePermissions';
import { getOtherScheduleDateSlug } from 'utils/workOrderScheduleDates';

import { ScheduleDatePicker } from './ScheduleDatePicker';

interface Props {
  field: AtlasGqlGlobalWorkContainerField;
  form: FormInstance<any>;
  uneditableFields: Record<string, React.ReactNode>;
  assetId?: string;
  containerType?: AtlasGqlWorkContainerTypeOption;
}

export function TaskFormInput({ field, form, uneditableFields, assetId, containerType }: Props) {
  const testId = `task-input-${field.slug}`;

  // These form inputs are also used by the Create Work Order wizard.
  // This variable is needed to help identify which parent container selector to use
  const containerTypeToConst =
    containerType === AtlasGqlWorkContainerTypeOption.WorkOrder ? WORK_CAMPAIGN : WORK_ORDER;

  if (field.slug === 'tags') {
    return (
      <TagChooser
        data-testid={testId}
        onChange={(tags: string[]) => form.setFieldsValue({ tags })}
      />
    );
  }

  if (field.slug === 'status') {
    return (
      <Select
        data-testid={testId}
        onChange={status => form.setFieldsValue({ status })}
        options={Object.values(TASK_STATUSES).map(status => ({
          value: status,
          label: startCase(status),
        }))}
      />
    );
  }

  if (field.slug === 'parent') {
    return (
      <WorkContainerChooser
        containerId={null}
        type={containerTypeToConst}
        labelInValue={false}
        onChange={(parent: string | null) => {
          form.setFieldsValue({ parent });
        }}
        testId={testId}
      />
    );
  }

  if (field.slug === 'for_organization') {
    return (
      <DeliveryOrganizationChooser
        formKey={field.slug}
        form={form}
        assetIds={assetId ? [assetId] : []}
        testId={testId}
      />
    );
  }

  if (field.slug === 'collaborators') {
    return (
      <RenderWithAccess rules={[WORK_ORDER_EDIT_COLLABORATORS]}>
        <Form.Item name={field.slug} noStyle={true}>
          <CollaboratorChooser />
        </Form.Item>
      </RenderWithAccess>
    );
  }

  const disabled = Boolean(uneditableFields[field.slug]);
  let input: React.ReactNode;

  if (field.type === AtlasGqlWorkContainerFieldType.Text) {
    input = <Input data-testid={testId} disabled={disabled} />;
  }
  if (field.type === AtlasGqlWorkContainerFieldType.Number) {
    input = <InputNumber data-testid={testId} />;
  }
  if (field.type === AtlasGqlWorkContainerFieldType.Date) {
    const otherScheduleDateSlug = getOtherScheduleDateSlug(field.slug);
    input = otherScheduleDateSlug ? (
      <ScheduleDatePicker
        slug={field.slug}
        otherSlug={otherScheduleDateSlug}
        form={form}
        testId={testId}
        disabled={disabled}
      />
    ) : (
      <UTCDatePicker data-testid={testId} disabled={disabled} />
    );
  }
  if (field.type === AtlasGqlWorkContainerFieldType.Select) {
    input = (
      <Select
        allowClear={true}
        data-testid={testId}
        options={field.options?.map((option: string) => ({ value: option, label: option }))}
      />
    );
  }

  if (input) {
    // we must wrap the input in a Form.Item with a name and noStyle since it is
    // not a direct child of Form.Item as defined in FormInputs.tsx
    return (
      <Form.Item name={field.slug} noStyle={true}>
        {input}
      </Form.Item>
    );
  }

  return null;
}
